import { createContext, useContext, type ReactNode } from 'react';

/** Formats a number for display. Supplied by the app so EDS can render locale-aware values without reading formatting locale directly. */
export type NumberFormatter = (value: number) => string;

const defaultFormatNumber: NumberFormatter = (value) => String(value);

const NumberFormatContext = createContext<NumberFormatter | undefined>(
  undefined,
);

/**
 * Provides a number formatter to EDS components that display numeric values.
 * `packages/eds` stays locale-agnostic: the app injects its formatting-locale
 * aware formatter here (see the app-side adapter), and components consume it
 * via {@link useNumberFormat}. Without a provider, values fall back to
 * `String(value)` (e.g. tests, Storybook).
 */
export function NumberFormatProvider({
  formatNumber,
  children,
}: {
  formatNumber: NumberFormatter;
  children: ReactNode;
}) {
  return (
    <NumberFormatContext.Provider value={formatNumber}>
      {children}
    </NumberFormatContext.Provider>
  );
}

/** The current number formatter, or a `String` fallback when no provider is mounted. */
export function useNumberFormat(): NumberFormatter {
  return useContext(NumberFormatContext) ?? defaultFormatNumber;
}
